/**
 * appDownloadLink — "Get the app" destinations that keep a pending
 * creator code in the funnel as long as possible.
 *
 * WHEN THIS MATTERS. Only for visitors who have NOT signed up yet. Once
 * an account exists, the creator is already stamped on the user row and
 * the install is irrelevant to attribution — they just log in and their
 * attribution comes with them, whether that's ten minutes or ten months
 * later. The deterministic path is signing up HERE, on the web, with the
 * code present; that's the whole point of the website-first funnel.
 *
 * Routing CTAs through /c/<code> (the Pages Function,
 * src/web/functions/c/[code].ts) keeps the code visible right up to the
 * store handoff. Since Singular was removed (2026-09-01) nothing carries
 * a code ACROSS a store install — a visitor who skips web signup and
 * installs is unattributed unless they later type the code in the app.
 * Accepted trade; the funnel pushes signup first for exactly this reason.
 *
 * We deliberately do NOT consume the stored code here (peek, not take):
 * the visitor may still come back and sign up on the web, and that path
 * is the exact one we'd rather have win.
 */
import { peekPendingReferralCode } from './referral';
import { GET_THE_APP_URL } from './stores';

/**
 * Origin for the /c/<code> redirect. Uses the current origin so this
 * works unchanged on dev.wydm.app and on a local dev server; falls back
 * to the canonical /get URL's origin during SSR/prerender, where
 * `window` is absent (the crawler-UA Pages Function path).
 */
function origin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return new URL(GET_THE_APP_URL).origin;
}

/**
 * The href every "Get the app" CTA should use.
 *
 * With a pending creator code → `/c/<code>`, which attributes the install.
 * Without one → the plain `/get` smart-redirect, exactly as before.
 *
 * Safe to call during render: it only reads localStorage, and failures
 * there degrade to the unattributed URL rather than throwing.
 */
export function appDownloadHref(): string {
  const code = peekPendingReferralCode();
  if (!code) return GET_THE_APP_URL;
  return `${origin()}/c/${encodeURIComponent(code)}`;
}

/**
 * Whether the current visitor has a creator code attached. Lets a CTA
 * decide whether an install is worth attributing at all — used by /get to
 * skip its own user-agent sniffing and hand off to /c/<code>, which does
 * the platform split on the edge with the code intact.
 */
export function hasPendingAttribution(): boolean {
  return peekPendingReferralCode() !== undefined;
}
